import {Link, useUrl, useCart} from '@shopify/hydrogen';
import {useState, useEffect} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Transition} from '@headlessui/react';
import Slider from '@moredeal/react-slick';

import {
  IconAccount,
  IconBag,
  IconMenu,
  IconClose,
  IconStore,
  Navigation,
  AlgoliaSearch,
  AlgoliaProvider,
} from '~/components';

import {CartDrawer} from './CartDrawer.client';
import {useDrawer} from './Drawer.client';

import type {EnhancedMenu} from '~/lib/utils';
import type {
  SanityCollectionTree,
  SanityPageMenuLinkItem,
} from '~/types/sanityTypes';

import urlBuilder from '@sanity/image-url';
import {
  useSanityClient,
  convertCollectionTreeToInternalLinkArray,
} from '~/lib/utils';

/**
 * A client component that specifies the content of the header on the website
 */

export function Header({
  menu,
  settings,
  collections,
  pageMenu,
  vendorsCollectionSlug,
}: {
  settings: any;
  menu?: EnhancedMenu;
  collections: SanityCollectionTree[];
  pageMenu: SanityPageMenuLinkItem[];
  vendorsCollectionSlug: string;
}) {
  const {pathname} = useUrl();

  const localeMatch = /^\/([a-z]{2})(\/|$)/i.exec(pathname);
  const countryCode = localeMatch ? localeMatch[1] : undefined;
  const isHome = pathname === `/${countryCode ? countryCode + '/' : ''}`;
  const headerLogo = settings ? settings.headerLogo : undefined;
  const mobileLogo = settings ? settings.mobileLogo : undefined;
  const collectionNav = collections ? collections : undefined;
  const storeMessages = settings ? settings.storeSettings?.messages : null;
  const storeMessageDuration = settings
    ? settings.storeSettings?.interval
    : 6000;

  const {
    isOpen: isCartOpen,
    openDrawer: openCart,
    closeDrawer: closeCart,
  } = useDrawer();

  return (
    <>
      <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
      <DesktopHeader
        countryCode={countryCode}
        isHome={isHome}
        openCart={openCart}
        logo={headerLogo}
        collections={collectionNav}
        pageMenu={pageMenu}
        vendorsCollectionSlug={vendorsCollectionSlug}
        storeMessages={storeMessages}
        storeMessageDuration={storeMessageDuration}
      />
      <MobileHeader
        countryCode={countryCode}
        isHome={isHome}
        openCart={openCart}
        logo={mobileLogo}
        collections={collectionNav}
        pageMenu={pageMenu}
        vendorsCollectionSlug={vendorsCollectionSlug}
        storeMessages={storeMessages}
        storeMessageDuration={storeMessageDuration}
      />
    </>
  );
}

function MobileHeader({
  countryCode,
  isHome,
  openCart,
  collections,
  logo,
  pageMenu,
  vendorsCollectionSlug,
  storeMessages,
  storeMessageDuration,
}: {
  logo?: any;
  collections?: SanityCollectionTree[];
  countryCode?: string | null;
  isHome: boolean;
  pageMenu?: SanityPageMenuLinkItem[];
  openCart: () => void;
  vendorsCollectionSlug: string;
  storeMessages: string[] | null;
  storeMessageDuration: number;
}) {
  return (
    <header role="banner" className="mobile-header lg:hidden relative">
      {storeMessages && (
        <StoreMessages
          messages={storeMessages}
          duration={storeMessageDuration}
        />
      )}
      <div className="bg-black h-16 flex items-center px-4 pb-2 sm:pb-0">
        <div className="grid grid-cols-mobile-header items-center justify-start gap-4">
          <MobileMenu
            logo={logo}
            pageMenu={pageMenu}
            collections={collections}
            openCart={openCart}
            isHome={isHome}
            vendorsCollectionSlug={vendorsCollectionSlug}
          />
        </div>
        <Link className="inline-flex w-full items-center" to="/">
          <Logo logo={logo} />
        </Link>
        <div className="inline-flex items-center text-white gap-4 justify-end w-full">
          <Link to={'/sivu/yhteystiedot'} className="store text-white">
            <IconStore />
          </Link>
          <Link to={'/account'} className="account text-white">
            <IconAccount />
          </Link>
          <button onClick={openCart} className="cart text-white relative">
            <IconBag />
            <CartBadge dark={isHome} />
          </button>
        </div>
      </div>

      <AlgoliaProvider>
        <AlgoliaSearch mobile={true} />
      </AlgoliaProvider>

      {/* <form
        action={`/${countryCode ? countryCode + '/' : ''}search`}
        className="bg-black relative items-center pr-4 pl-4 pb-4 gap-2 sm:flex"
      >
        <button type="submit" className="absolute">
          <IconSearch />
        </button>
        <Input
          className={
            isHome
              ? 'w-full focus:border-contrast/20 dark:focus:border-primary/20 text-sm'
              : 'w-full focus:border-primary/20 text-sm'
          }
          type="search"
          variant="minisearch"
          placeholder="Hae tuotteita, kategorioita tai artikkeleita"
          name="q"
        />
      </form> */}
    </header>
  );
}

function MobileMenu({
  collections,
  logo,
  pageMenu,
  openCart,
  isHome,
  vendorsCollectionSlug,
}: {
  openCart: () => void;
  isHome: boolean;
  collections?: SanityCollectionTree[];
  pageMenu?: SanityPageMenuLinkItem[];
  logo?: any;
  vendorsCollectionSlug: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const updateIsOpen = (newValue: boolean) => {
    setIsOpen(newValue);
  };

  return (
    <div className="flex h-4">
      <button
        className="text-white"
        onClick={(e) => {
          updateIsOpen(true);
        }}
      >
        <IconMenu></IconMenu>
      </button>
      <Transition
        show={isOpen}
        className="fixed top-0 left-0 w-full h-full bg-opacity-50 z-50"
        enter="transition-all duration-300"
        enterFrom="transform -translate-x-full"
        enterTo="transform -translate-x-0"
        leave="transition-all opacity-100 duration-100"
        leaveFrom="transform opacity-100 -translate-x-0"
        leaveTo="transform opacity-0 -translate-x-full"
      >
        <MobileMenuList
          pageMenu={pageMenu}
          collections={collections}
          logo={logo}
          setIsOpen={updateIsOpen}
          openCart={openCart}
          isHome={isHome}
          vendorsCollectionSlug={vendorsCollectionSlug}
        />
      </Transition>
    </div>
  );
}

function MobileMenuList({
  collections,
  logo,
  pageMenu,
  setIsOpen,
  openCart,
  isHome,
  vendorsCollectionSlug,
}: {
  openCart: () => void;
  isHome: boolean;
  setIsOpen: (isOpen: boolean) => void;
  collections?: SanityCollectionTree[];
  pageMenu?: SanityPageMenuLinkItem[];
  logo?: any;
  vendorsCollectionSlug: string;
}) {
  const mobileLinks = () => {
    return [
      ...(collections
        ? convertCollectionTreeToInternalLinkArray(
            collections,
            vendorsCollectionSlug,
          )
        : []),
      ...(pageMenu ? pageMenu : []),
    ];
  };

  return (
    <div className="mobile-menu-list h-screen left-0 top-0 fixed bg-white z-30">
      <div className="bg-black flex items-center justify-start w-full h-16 px-4">
        <Link
          className="flex items-center justify-start leading-[3rem] md:leading-[4rem] flex-grow w-full h-full"
          to="/"
        >
          <Logo logo={logo} />
        </Link>

        <button
          className="text-white"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          <IconClose></IconClose>
        </button>
      </div>

      <div className="overflow-y-scroll h-screen pb-[20vh]">
        <Navigation
          menuSide="mobile-menu"
          links={mobileLinks()}
          mobile
          setIsOpen={setIsOpen}
        ></Navigation>
        <div className="utility p-4 flex flex-col w-full">
          <Link
            className="gap-2 store mb-4 text-sm items-center inline-flex"
            to={'/store'}
          >
            <IconStore />
            Myymälä
          </Link>
          <Link
            className="gap-2 account mb-4 text-sm items-center inline-flex"
            to={'/account'}
          >
            <IconAccount />
            Asiakastili
          </Link>
          <button
            className="gap-1 cart mb-4 text-sm items-center inline-flex relative"
            onClick={openCart}
          >
            <IconBag />
            <CartBadge dark={isHome} />
            Ostoskori
          </button>
        </div>
        <div
          className="close-area"
          onClick={() => {
            setIsOpen(false);
          }}
        ></div>
      </div>
    </div>
  );
}

function DesktopHeader({
  countryCode,
  isHome,
  openCart,
  logo,
  collections,
  pageMenu,
  vendorsCollectionSlug,
  storeMessages,
  storeMessageDuration,
}: {
  logo?: any;
  collectionSettings?: any;
  collections?: SanityCollectionTree[];
  countryCode?: string | null;
  isHome: boolean;
  pageMenu?: SanityPageMenuLinkItem[];
  vendorsCollectionSlug: string;
  openCart: () => void;
  storeMessages: string[] | null;
  storeMessageDuration: number;
}) {
  const [stickyClass, setStickyClass] = useState('relative');

  useEffect(() => {
    window.addEventListener('scroll', stickNavbar);

    return () => {
      window.removeEventListener('scroll', stickNavbar);
    };
  }, []);

  const stickNavbar = () => {
    if (window !== undefined) {
      const windowHeight = window.scrollY;
      const breakPoint = storeMessages?.length ? 105 : 72;
      windowHeight > breakPoint
        ? setStickyClass('fixed z-50 top-0 w-full')
        : setStickyClass('relative');
    }
  };

  return (
    <header role="banner" className="lg:block hidden desktop-header relative">
      {storeMessages && (
        <StoreMessages
          messages={storeMessages}
          duration={storeMessageDuration}
        />
      )}
      <div className="bg-bgGray flex h-20 items-center">
        <div className="grid gap-2 h-full items-center grid-cols-topbar-header container mx-auto">
          {logo && (
            <Link className={`font-bold mr-5`} to="/">
              <Logo logo={logo} />
            </Link>
          )}
          <AlgoliaProvider>
            <AlgoliaSearch />
          </AlgoliaProvider>
          <div className="grid gap-6 h-5 grid-cols-3 w-full">
            <Link
              to={'/sivu/yhteystiedot'}
              className="store relative gap-1.5 flex items-center justify-center focus:ring-primary/5 text-xs"
            >
              <IconStore />
              Myymälä
            </Link>
            <Link
              to={'/account'}
              className="account relative gap-1.5 flex items-center justify-center focus:ring-primary/5 text-xs"
            >
              <IconAccount />
              Asiakastili
            </Link>
            <button
              onClick={openCart}
              className="cart relative gap-1.5 flex items-center justify-center focus:ring-primary/5 text-xs"
            >
              <IconBag />
              <CartBadge dark={isHome} />
              Ostoskori
            </button>
          </div>
        </div>
      </div>

      <div className={`bg-black ${stickyClass}`}>
        <div className="flex gap-12 container items-center h-12">
          {collections ? (
            <Navigation
              mobile={false}
              menuSide="left-menu justify-start"
              links={convertCollectionTreeToInternalLinkArray(
                collections,
                vendorsCollectionSlug,
              )}
            />
          ) : null}
          {pageMenu ? (
            <Navigation
              mobile={false}
              menuSide="right-menu justify-end"
              links={pageMenu}
            />
          ) : null}
        </div>
      </div>
    </header>
  );
}

function StoreMessages({
  messages,
  duration,
}: {
  messages: string[];
  duration: number;
}) {
  const sliderSettings = {
    autoplay: true,
    autoplaySpeed: duration,
    arrows: false,
    infinite: true,
    fade: true,
    speed: 1000,
  };

  return (
    <div className="store-messages bg-black text-center text-white text-xs py-2">
      <Slider {...sliderSettings}>
        {messages.map((message, index) => (
          <span
            key={'storeMessage' + index}
            dangerouslySetInnerHTML={{__html: message}}
          ></span>
        ))}
      </Slider>
    </div>
  );
}

function Logo({logo}: {logo: any}) {
  const setLogo = logo
    ? urlBuilder(useSanityClient)
        .image(logo)
        .width(195)
        .fit('max')
        .auto('format')
        .url()
    : undefined;

  return (
    <img
      src={setLogo}
      alt={'Mareksound header logo'}
      loading="eager"
      width={195}
      style={{
        display: 'block',
      }}
    />
  );
}

function CartBadge({dark}: {dark: boolean}) {
  const {totalQuantity} = useCart();

  if (totalQuantity < 1) {
    return null;
  }
  return (
    <div className={'absolute left-3.5 bottom-0'}>
      <span className={'rounded bg-accentRed block'}></span>
    </div>
  );
}
